import { httpsCallable } from "firebase/functions";
import { FormEvent, useState } from "react";
import { trackCta } from "../analytics";
import { functions } from "../firebase";
import { Section } from "./Section";

type InquiryPayload = {
  parentName: string;
  email: string;
  phone: string;
  studentNames: string;
  studentGrades: string;
  desiredStart: string;
  currentSchool: string;
  preferredContact: string;
  inquiryType: string;
  message: string;
  sourcePath?: string;
  sourceTitle?: string;
};

const initialForm: InquiryPayload = {
  parentName: "",
  email: "",
  phone: "",
  studentNames: "",
  studentGrades: "",
  desiredStart: "",
  currentSchool: "",
  preferredContact: "Email",
  inquiryType: "Schedule a Visit",
  message: "",
};

export function InquiryForm() {
  const [form, setForm] = useState<InquiryPayload>(initialForm);
  const [status, setStatus] = useState<"idle" | "saving" | "sent">("idle");
  const [error, setError] = useState("");

  function update<K extends keyof InquiryPayload>(key: K, value: InquiryPayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!functions) {
      setError("Inquiry form is not configured yet. Please email the school office.");
      return;
    }

    setStatus("saving");
    setError("");
    try {
      const fn = httpsCallable(functions, "submitPublicInquiry");
      await fn({
        ...form,
        sourcePath: window.location.pathname,
        sourceTitle: document.title,
      });
      trackCta("schedule_visit", "Inquiry form submitted");
      setStatus("sent");
      setForm(initialForm);
    } catch (err: any) {
      setError(err?.message || "Could not send your inquiry. Please try again or email the school office.");
      setStatus("idle");
    }
  }

  return (
    <Section eyebrow="Schedule a Visit" title="Start a conversation with ZCA" className="band-warm">
      <form className="inquiry-form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            Parent or guardian name
            <input value={form.parentName} onChange={(event) => update("parentName", event.target.value)} required />
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={(event) => update("email", event.target.value)} required />
          </label>
          <label>
            Phone
            <input type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} />
          </label>
          <label>
            Student name(s)
            <input value={form.studentNames} onChange={(event) => update("studentNames", event.target.value)} placeholder="Optional" />
          </label>
          <label>
            Student grade or age
            <input value={form.studentGrades} onChange={(event) => update("studentGrades", event.target.value)} placeholder="Pre-K, Kindergarten, 2nd grade..." />
          </label>
          <label>
            Desired start
            <input value={form.desiredStart} onChange={(event) => update("desiredStart", event.target.value)} placeholder="Fall 2026, as soon as possible..." />
          </label>
          <label>
            Current school
            <input value={form.currentSchool} onChange={(event) => update("currentSchool", event.target.value)} placeholder="Optional" />
          </label>
          <label>
            Preferred contact
            <select value={form.preferredContact} onChange={(event) => update("preferredContact", event.target.value)}>
              <option>Email</option>
              <option>Phone</option>
              <option>Either</option>
            </select>
          </label>
          <label>
            I would like to
            <select value={form.inquiryType} onChange={(event) => update("inquiryType", event.target.value)}>
              <option>Schedule a Visit</option>
              <option>Ask a Question</option>
              <option>Request Admissions Information</option>
              <option>Learn About Tuition</option>
              <option>Other</option>
            </select>
          </label>
        </div>
        <label>
          What would you like to know?
          <textarea rows={5} value={form.message} onChange={(event) => update("message", event.target.value)} required />
        </label>
        {error ? <p className="notice-light">{error}</p> : null}
        {status === "sent" ? <p className="notice-light">Thank you. Your inquiry has been sent to Zion Classical Academy.</p> : null}
        <button className="button button-gold" type="submit" disabled={status === "saving"}>
          {status === "saving" ? "Sending..." : "Send Inquiry"}
        </button>
      </form>
    </Section>
  );
}
